// File: app/src/hooks/useTotalDuration.js
//
// Thin wrappers around the workout compiler that automatically thread the
// active user's `training_data` and aggregated history map through to the rep
// estimator. Use these anywhere session time is displayed — they keep call
// sites from each having to reach into two contexts.

import { useMemo } from "react";
import { useUserProfile } from "../contexts/UserProfileContext";
import { calculateTotalDuration } from "../utils/time";
import { summariseGroupRounds } from "../utils/workoutCompiler";
import { deriveTrainingData } from "../utils/performanceProfile";
import useExerciseHistory from "./useExerciseHistory";

// The `{ profile, historyMap }` pair every compiler entry point takes. Memoised
// so the hooks below keep a stable dependency and don't recompile on each render.
const useEstimatorInputs = () => {
  const { profile, performanceCapabilities } = useUserProfile();
  const historyMap = useExerciseHistory();
  return useMemo(() => {
    const trainingData = deriveTrainingData(performanceCapabilities, {
      pace: profile?.training_data?.pace,
      fallbackTrainingData: profile?.training_data,
    });
    return { profile: { training_data: trainingData }, historyMap };
  }, [profile, performanceCapabilities, historyMap]);
};

const useTotalDuration = (plan, { skipLastRest = true } = {}) => {
  const inputs = useEstimatorInputs();
  return useMemo(
    () => calculateTotalDuration(plan, { skipLastRest, ...inputs }),
    [plan, skipLastRest, inputs],
  );
};

// Per-round breakdown of a multi-round Group — the real duration of each round
// plus the changeover and stripped-rest time that reconciles those rounds with
// the block total (issue #73). Returns the empty summary for anything that is
// not a multi-round Group, so it is safe to call unconditionally.
export const useGroupRoundSummary = (group) => {
  const inputs = useEstimatorInputs();
  return useMemo(() => summariseGroupRounds(group, inputs), [group, inputs]);
};

export default useTotalDuration;
